import { useTranslation } from "react-i18next";
import { Modal } from "../admin/Modal";
import type { BedrockTakeoverPreview } from "../../api/bedrockModels";
import styles from "./BedrockModels.module.css";

/**
 * The confirmation of taking over a group whose policy the portal did not write (AIA-109 phase 4b). It says plainly that the existing
 * policy will be replaced, and shows what appears to be removed and added. Those two lists come from a best-effort reading of the old
 * policy: they are labels to help the decision, never what the server decides from.
 */
export function TakeoverDialog({
  group,
  preview,
  selectedCount,
  busy,
  onConfirm,
  onCancel,
}: {
  group: string;
  preview: BedrockTakeoverPreview;
  selectedCount: number;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Modal title={t("bedrockModels.takeover.title", { group })} onClose={onCancel} closeOnBackdropClick={false}>
      <p>
        <strong>{t("bedrockModels.takeover.replaces", { group })}</strong> {t("bedrockModels.takeover.recorded")}
      </p>
      <p>{t("bedrockModels.takeover.selection", { count: selectedCount })}</p>
      {selectedCount === 0 && <p className={styles.overLimit}>{t("bedrockModels.takeover.empty")}</p>}
      <h3>{t("bedrockModels.takeover.removed", { count: preview.removedFromDisplay.length })}</h3>
      {preview.removedFromDisplay.length === 0 ? (
        <p>{t("bedrockModels.takeover.none")}</p>
      ) : (
        <ul className={styles.idList}>
          {preview.removedFromDisplay.map((id) => (
            <li key={id}>
              <code>{id}</code>
            </li>
          ))}
        </ul>
      )}
      <h3>{t("bedrockModels.takeover.added", { count: preview.addedFromDisplay.length })}</h3>
      {preview.addedFromDisplay.length === 0 ? (
        <p>{t("bedrockModels.takeover.none")}</p>
      ) : (
        <ul className={styles.idList}>
          {preview.addedFromDisplay.map((id) => (
            <li key={id}>
              <code>{id}</code>
            </li>
          ))}
        </ul>
      )}
      <p>{t("bedrockModels.takeover.labelsOnly")}</p>
      <p>{t("bedrockModels.takeover.size", { size: preview.size, limit: preview.limit })}</p>
      <div className={styles.dialogActions}>
        <button type="button" className="button button-secondary" onClick={onCancel} disabled={busy} data-autofocus>
          {t("bedrockModels.cancel")}
        </button>
        <button type="button" className="button button-primary" onClick={onConfirm} disabled={busy}>
          {t("bedrockModels.takeover.confirm")}
        </button>
      </div>
    </Modal>
  );
}
